/**
 * A very small entity-component store.
 *
 * Deferred until now on purpose: with one entity — Katie — it was ceremony,
 * and a farm needed no more. Enemies arrive in numbers, die, and are joined by
 * projectiles in the same phase, so the thing an ECS is actually for finally
 * exists: several kinds of short-lived thing sharing position, health and
 * collision without sharing a class hierarchy.
 *
 * Archetype-free and Map-backed, as the plan called for. There is no query
 * planner and no system scheduler here — iterating a Map of a few dozen
 * enemies costs nothing, and a scheduler with three systems in it is a
 * framework nobody asked for. Add one when the profiler says to.
 */

export type Entity = number;

export class EntityWorld {
  private next: Entity = 1;
  private readonly living = new Set<Entity>();
  private readonly stores: { remove: (entity: Entity) => void }[] = [];

  create(): Entity {
    const entity = this.next++;
    this.living.add(entity);
    return entity;
  }

  /**
   * Destroy an entity and every component it owns.
   *
   * Stores register themselves so this cannot drift: adding a component type
   * and forgetting to clean it up on death is the classic leak, and it shows
   * up as a ghost that still takes damage.
   */
  destroy(entity: Entity): void {
    if (!this.living.delete(entity)) return;
    for (const store of this.stores) store.remove(entity);
  }

  alive(entity: Entity): boolean {
    return this.living.has(entity);
  }

  get count(): number {
    return this.living.size;
  }

  /** Live entities, safe to destroy while iterating the returned array. */
  entities(): Entity[] {
    return [...this.living];
  }

  register<T>(store: ComponentStore<T>): ComponentStore<T> {
    this.stores.push(store);
    return store;
  }

  clear(): void {
    for (const entity of this.entities()) this.destroy(entity);
    this.next = 1;
  }
}

export class ComponentStore<T> {
  private readonly items = new Map<Entity, T>();

  set(entity: Entity, value: T): T {
    this.items.set(entity, value);
    return value;
  }

  get(entity: Entity): T | undefined {
    return this.items.get(entity);
  }

  has(entity: Entity): boolean {
    return this.items.has(entity);
  }

  remove(entity: Entity): void {
    this.items.delete(entity);
  }

  get size(): number {
    return this.items.size;
  }

  entries(): [Entity, T][] {
    return [...this.items];
  }

  values(): T[] {
    return [...this.items.values()];
  }
}
